
import { getTechniciansList, createTechnicianItem, updateTechnicianItem } from '@/api';
import { Component, Vue } from 'vue-property-decorator';
import { User } from '@/models/main';
import { mapState } from 'vuex';
import { timeZoneDefault } from '@/utils/PortalConstants';
import { format, utcToZonedTime } from 'date-fns-tz';
import { notifyNetworkError } from '@/helpers/notifiers';

import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import Icon from '@/components/UI/Icon.vue';
import Header from '@/components/jobBooking/Header/Header.vue';
import { TechnicianCatalogItem } from '@/models/technicians';

export interface TechnicianData {
  full_name: string;
  state: string;
  mobile_phone: string;
  notify_by_email: boolean;
  notify_by_sms: boolean;
  is_active: boolean;
}

@Component({
  components: {
    Button,
    BackButton,
    Header,
    Icon,
  },
  computed: {
    ...mapState(['availableStates']),
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class TechniciansCatalog extends Vue {
  private loading = false;
  private pendingForm = false;
  private user!: User;
  public format = format;
  public utcToZonedTime = utcToZonedTime;
  private availableStates: string[];

  public tableHeaders = [
    { text: 'Is active', value: 'is_active', align: '' },
    { text: 'Name', value: 'full_name', align: '' },
    { text: 'Notify by email', value: 'notify_by_email', align: '' },
    { text: 'Notify by phone', value: 'notify_by_sms', align: '' },
    { text: 'Email', value: 'email', align: '' },
    { text: 'Mobile phone', value: 'mobile_phone', align: '' },
    { text: 'Customer', value: 'customer', align: '' },
    { text: 'State', value: 'state', align: '' },
    { text: 'Edit', value: 'edit', align: '', sortable: false },
  ];

  public currentTechnician: TechnicianCatalogItem | null = null;
  public isNewTechnician = false;
  public technicianDefault: TechnicianCatalogItem = {
    full_name: '',
    state: '',
    mobile_phone: '',
    notify_by_email: true,
    notify_by_sms: true,
    is_active: true,
    id: 0,
    customer: '',
    email: '',
    created_at: '',
    updated_at: '',
  };

  get userTimeZone() {
    return this.user?.timezone || timeZoneDefault;
  }

  public isDialogOpen = false;
  public technicians: TechnicianCatalogItem[] = [];

  public draggingTechnician: TechnicianData | null = null;

  public handleOpenCreateTechnicianClicked(): void {
    const form = this.$refs.form as HTMLFormElement;
    if (form) {
      form.reset();
    }
    this.isNewTechnician = true;
    this.currentTechnician = { ...this.technicianDefault };
    this.isDialogOpen = true;
  }

  public handleOpenEditTechnicianClicked(technician: TechnicianCatalogItem): void {
    this.isNewTechnician = false;
    this.currentTechnician = { ...technician };
    this.isDialogOpen = true;
  }

  public handleTechnicianCreateClicked(): void {
    const form = this.$refs.form as Vue & { validate: () => boolean };
    const isValid = form.validate();
    if (isValid) {
      this.pendingForm = true;
      createTechnicianItem(this.currentTechnician)
        .then(res => {
          this.technicians.push(res.data);
        })
        .catch(() => {
          notifyNetworkError({ text: 'Error creating technician' });
        })
        .finally(() => {
          this.pendingForm = false;
        });
    }
  }

  public handleTechnicianUpdateClicked(): void {
    const form = this.$refs.form as Vue & { validate: () => boolean };
    const isValid = form.validate();
    if (isValid && this.currentTechnician) {
      this.pendingForm = true;
      updateTechnicianItem(this.currentTechnician.id, this.currentTechnician)
        .then(res => {
          const technician = this.technicians.find(x => x.id === res.data.id);
          if (technician) {
            Object.assign(technician, res.data);
          }
        })
        .catch(() => {
          notifyNetworkError({ text: 'Error updating technician' });
        })
        .finally(() => {
          this.pendingForm = false;
        });
    }
  }

  private async getDraftsWrapper() {
    this.loading = true;

    await getTechniciansList()
      .then(res => {
        this.technicians = res.data;
      })
      .catch(() => {
        notifyNetworkError({ text: 'Error loading technicians catalog' });
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private mounted(): void {
    this.getDraftsWrapper();
  }
}
